import { Box, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import type { SelectFieldOption } from '@/components/inputs/CustomSelectField'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import { BidType } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import getBidConfig from '@/features/bidding/utils/getBidConfig'

export type PtuRange = Readonly<{
  fromIndex: number
  toIndex: number
}>

export type PtuRangeSelectorProps = Readonly<{
  deliveryDay: MarketDate
  range: PtuRange
  onChange: (next: PtuRange) => void
  onResetToNow: () => void
}>

/**
 * Two dropdowns (From / To) + a "Now → End" quick reset.
 * Lists all 15-minute slots of the selected delivery day. Index-based so DST
 * day-length variations (92 / 96 / 100 slots) are handled by `MarketDate.ptus()`
 */
export default function PtuRangeSelector({ deliveryDay, range, onChange, onResetToNow }: PtuRangeSelectorProps) {
  const { t } = useTranslation()
  const { selectedCountry } = useBiddingContext()
  const ptuLengthInMinutes = getBidConfig(selectedCountry, BidType.ENERGY).ptuLengthInMin
  const ptus = useMemo(() => deliveryDay.ptus({ minutes: ptuLengthInMinutes }), [deliveryDay, ptuLengthInMinutes])

  const options = useMemo<SelectFieldOption<number>[]>(
    () =>
      ptus.map((ptu, index) => ({
        id: `ptu-${index}`,
        label: ptu.start.toFormat('HH:mm'),
        value: index,
      })),
    [ptus],
  )

  const handleFromChange = (value: number) => {
    onChange({ fromIndex: value, toIndex: Math.max(value, range.toIndex) })
  }

  const handleToChange = (value: number) => {
    onChange({ fromIndex: Math.min(range.fromIndex, value), toIndex: value })
  }

  return (
    <Stack alignItems="flex-end" direction="row" spacing={1}>
      <CustomSelectField<number>
        id="bid-detail-ptu-from"
        label={t('bidding.results.ptu_from')}
        options={options}
        size="small"
        sx={{ minWidth: 110 }}
        value={range.fromIndex}
        onChange={(event) => handleFromChange(Number(event.target.value))}
      />
      <Box sx={{ pb: 1 }}>
        <Typography color="text.secondary" variant="body2">
          →
        </Typography>
      </Box>
      <CustomSelectField<number>
        id="bid-detail-ptu-to"
        label={t('bidding.results.ptu_to')}
        options={options}
        size="small"
        sx={{ minWidth: 110 }}
        value={range.toIndex}
        onChange={(event) => handleToChange(Number(event.target.value))}
      />
      <CustomButton size="small" sx={{ pt: 0.6, pb: 0.6, pl: 2, pr: 2 }} variant="outlined" onClick={onResetToNow}>
        {t('bidding.results.now_to_end')}
      </CustomButton>
    </Stack>
  )
}
